import {
  Grid,
  Box,
  TextField,
  Button,
  Switch,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { Loading } from "../../components/shared/loading";
import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertPhotoOutlinedIcon from "@material-ui/icons/InsertPhotoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SaveIcon from "@material-ui/icons/Save";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";
const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6];

export const UpdateClient = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [priority, setPriority] = useState("");
  const [clientPic, setClientPic] = useState({ raw: "", preview: "" });
  const [status, setStatus] = useState(true);
  const [oldImage, setOldImage] = useState("");
  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const formData = new FormData();
    formData.append("idd", id);
    Axios.post("/api/select_single_client", formData, {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        const d = res.data.data;
        setUrl(d.site_url || "");
        setPriority(String(d.priority || ""));
        setName(d.name || "");
        if (d.image_url) setOldImage(d.image_url);
        setStatus(d.status === 1);
        setIsLoading(false);
      } else {
        toast.error("Could not load client");
        navigate("/dash/allclients");
      }
    });
  }, [id, navigate, toast]);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please select only JPEG, JPG, PNG or WebP images.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB.");
      return;
    }
    setClientPic({
      preview: URL.createObjectURL(file),
      raw: file,
    });
  };

  const onSubmit = () => {
    setErr(0);
    if (!name.trim()) {
      setErr(4);
      toast.error("Please enter the client name.");
      return;
    }
    if (!url.trim()) {
      setErr(1);
      toast.error("Please enter the website URL.");
      return;
    }
    if (!priority) {
      setErr(2);
      toast.error("Please select a priority.");
      return;
    }
    if (!clientPic.raw && !oldImage) {
      setErr(3);
      toast.error("Please select a client logo image.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    if (clientPic.raw) formData.append("imgurl", clientPic.raw);
    formData.append("idd", id);
    formData.append("name", name.trim());
    formData.append("siteurl", url.trim());
    formData.append("priority", priority);
    formData.append("status", status ? "1" : "0");

    Axios.post("/api/update_client", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Client updated");
          navigate("/dash/allclients");
        } else {
          toast.error(res.data.msg || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <Helmet>
        <title>Edit Client - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Clients"
        title="Edit client"
        subtitle="Update the client logo, URL, priority, and visibility."
        backTo="/dash/allclients"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client name"
            variant="outlined"
            error={err === 4}
            value={name}
            size="small"
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Website URL"
            variant="outlined"
            error={err === 1}
            value={url}
            size="small"
            onChange={(e) => setUrl(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Priority"
            variant="outlined"
            error={err === 2}
            value={priority}
            size="small"
            onChange={(e) => setPriority(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Select...</MenuItem>
            {PRIORITY_OPTIONS.map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.statusRow}>
            <Switch
              checked={status}
              onChange={() => setStatus(!status)}
              color="primary"
            />
            <span>
              Status:{" "}
              <span className={status ? "on" : "off"}>
                {status ? "Visible" : "Hidden"}
              </span>
            </span>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Logo image</Typography>
          <Box className={classes.uploadWrap}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
              id="client-image-upload"
              type="file"
            />
            <label htmlFor="client-image-upload" style={{ display: "block" }}>
              <Box
                className={`${classes.uploadBox} ${
                  clientPic.raw ? classes.uploadBoxFilled : ""
                }`}
              >
                <Box className={classes.uploadIconWrap}>
                  {clientPic.raw ? (
                    <InsertPhotoOutlinedIcon />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )}
                </Box>
                <Box className={classes.uploadInfo}>
                  {clientPic.raw ? (
                    <>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlineIcon
                          style={{ fontSize: 16, color: "#10b981" }}
                        />
                        <strong>{clientPic.raw.name}</strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        Click to choose a different file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <span>
                        <strong>
                          {oldImage ? "Replace logo" : "Upload client logo"}
                        </strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        JPEG, PNG or WebP, up to 5 MB
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </label>
            <Box style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {oldImage && !clientPic.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    Current logo
                  </Typography>
                  <img
                    src={"/static/clients/" + oldImage}
                    alt="current"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 200, maxHeight: 160, background: "#ffffff" }}
                  />
                </Box>
              )}
              {clientPic.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    New logo preview
                  </Typography>
                  <img
                    src={clientPic.preview}
                    alt="preview"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 200, maxHeight: 160, background: "#ffffff" }}
                  />
                </Box>
              )}
            </Box>
            {err === 3 && (
              <Typography style={{ color: "#dc2626", fontSize: "0.78rem" }}>
                Please select an image.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/allclients")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          startIcon={!submitting && <SaveIcon />}
        >
          {submitting ? "Updating..." : "Save changes"}
        </Button>
      </Box>
    </div>
  );
};
